import { log } from '@atlassian/jira-common-util-logging';
import { PACKAGE_LOCATION, SERVICE_WORKER_SCOPE } from '../../common/constants';

/**
 * This unregisters all service workers for the `/jira/software` scope. If there's a failure it'll
 * be logged.
 *
 * https://w3c.github.io/ServiceWorker/#navigator-service-worker-unregister
 *
 * The service worker will only be stopped once all tabs connected to it unload. The actual
 * kill-switch mechanism will be based on updates.
 *
 * @returns {Promise<void>}
 */
export const destroyServiceWorker = async () => {
	try {
		const serviceWorkerContainer: ServiceWorkerContainer | null =
			typeof navigator !== 'undefined' && navigator.serviceWorker ? navigator.serviceWorker : null;
		if (!serviceWorkerContainer || __SERVER__) {
			return;
		}

		const registrations = await serviceWorkerContainer.getRegistrations();
		const softwareServiceWorkers = Array.from(registrations).filter((registration) =>
			registration.scope.endsWith(SERVICE_WORKER_SCOPE),
		);
		const results = softwareServiceWorkers.map((registration) => registration.unregister());
		const successCount = (await Promise.all(results)).filter(
			(unregisterSuccessful) => unregisterSuccessful,
		).length;

		if (successCount !== softwareServiceWorkers.length) {
			log.safeWarnWithoutCustomerData(
				PACKAGE_LOCATION,
				`ServiceWorker unregister cleared ${successCount} of ${softwareServiceWorkers.length} registrations`,
			);
		} else if (successCount > 0) {
			log.safeInfoWithoutCustomerData(PACKAGE_LOCATION, 'ServiceWorker unregistered successfully');
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		log.safeErrorWithoutCustomerData(PACKAGE_LOCATION, 'ServiceWorker unregister error', err);
	}
};
